export const NCRM_CONTACTS_ADVANCED_FILTER_STORAGE_KEY = "ncrm-contacts-advanced-filters";

export const CONTACTS_FILTER_LOGIC_VALUES = Object.freeze(["and", "or"]);
export const CONTACTS_FILTER_OPERATOR_VALUES = Object.freeze([
  "equals",
  "not_equals",
  "contains",
  "not_contains",
  "is_empty",
  "is_not_empty",
]);
export const CONTACTS_FILTER_OPERATORS_WITHOUT_VALUE = Object.freeze(["is_empty", "is_not_empty"]);

const CONTACT_FILTER_TYPES = Object.freeze(["company", "person"]);

const normalizeText = (value) => String(value ?? "").trim();

const normalizeType = (value) => {
  const normalized = normalizeText(value).toLowerCase();
  return CONTACT_FILTER_TYPES.includes(normalized) ? normalized : null;
};

const normalizeLogic = (value, fallback = "and") => {
  const normalized = normalizeText(value).toLowerCase();
  if (CONTACTS_FILTER_LOGIC_VALUES.includes(normalized)) return normalized;
  return CONTACTS_FILTER_LOGIC_VALUES.includes(fallback) ? fallback : "and";
};

const normalizeOperator = (value, fallback = "equals") => {
  const normalized = normalizeText(value).toLowerCase();
  if (CONTACTS_FILTER_OPERATOR_VALUES.includes(normalized)) return normalized;
  return CONTACTS_FILTER_OPERATOR_VALUES.includes(fallback) ? fallback : "equals";
};

export const contactsFilterOperatorRequiresValue = (operator) =>
  !CONTACTS_FILTER_OPERATORS_WITHOUT_VALUE.includes(normalizeOperator(operator));

const normalizeCondition = (condition = {}) => {
  const fieldKey = normalizeText(condition.field_key ?? condition.fieldKey).toLowerCase();
  if (!fieldKey) return null;
  const operator = normalizeOperator(condition.operator);
  const value = normalizeText(condition.value ?? condition.term);
  if (contactsFilterOperatorRequiresValue(operator) && !value) return null;
  return {
    field_key: fieldKey,
    field_label: normalizeText(condition.field_label ?? condition.fieldLabel),
    field_group: normalizeText(condition.field_group ?? condition.fieldGroup).toLowerCase(),
    operator,
    value: contactsFilterOperatorRequiresValue(operator) ? value : "",
  };
};

export const normalizeContactsAdvancedFilters = (value = {}) => {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const rawConditions = Array.isArray(source.conditions)
    ? source.conditions
    : Array.isArray(source.rules)
      ? source.rules
      : [];
  return {
    logic: normalizeLogic(source.logic ?? source.conditions_logic ?? source.conditionsLogic, "and"),
    conditions: rawConditions.map(normalizeCondition).filter(Boolean),
  };
};

export const hasContactsAdvancedFilters = (value = {}) =>
  normalizeContactsAdvancedFilters(value).conditions.length > 0;

// Serializare pentru parametrul `filters` al GET /ncrm/contacts.
export const serializeContactsAdvancedFilters = (value = {}) => {
  const normalized = normalizeContactsAdvancedFilters(value);
  if (!normalized.conditions.length) return "";
  return JSON.stringify({
    logic: normalized.logic,
    conditions: normalized.conditions.map((condition) => ({
      field_key: condition.field_key,
      operator: condition.operator,
      value: condition.value,
    })),
  });
};

export const buildContactsFilterChipLabel = (condition = {}, { operatorLabels = {} } = {}) => {
  const normalized = normalizeCondition(condition);
  if (!normalized) return "";
  const fieldLabel = normalized.field_label || normalized.field_key;
  if (!contactsFilterOperatorRequiresValue(normalized.operator)) {
    const operatorLabel = normalizeText(operatorLabels[normalized.operator]) || normalized.operator;
    return `${fieldLabel}: ${operatorLabel}`;
  }
  if (normalized.operator === "equals") {
    return `${fieldLabel}: ${normalized.value}`;
  }
  const operatorLabel = normalizeText(operatorLabels[normalized.operator]) || normalized.operator;
  return `${fieldLabel} ${operatorLabel}: ${normalized.value}`;
};

const readStorageSnapshot = (storage) => {
  if (!storage || typeof storage.getItem !== "function") return {};
  try {
    const raw = storage.getItem(NCRM_CONTACTS_ADVANCED_FILTER_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
};

const writeStorageSnapshot = (storage, snapshot) => {
  if (!storage || typeof storage.setItem !== "function") return;
  try {
    storage.setItem(NCRM_CONTACTS_ADVANCED_FILTER_STORAGE_KEY, JSON.stringify(snapshot));
  } catch (_) {
    /* ignore storage issues */
  }
};

export const readContactsAdvancedFilters = (type, storage = globalThis?.localStorage) => {
  const normalizedType = normalizeType(type);
  if (!normalizedType) return normalizeContactsAdvancedFilters();
  const snapshot = readStorageSnapshot(storage);
  return normalizeContactsAdvancedFilters(snapshot?.[normalizedType]);
};

export const writeContactsAdvancedFilters = (type, filters, storage = globalThis?.localStorage) => {
  const normalizedType = normalizeType(type);
  if (!normalizedType) return normalizeContactsAdvancedFilters();
  const snapshot = readStorageSnapshot(storage);
  const normalizedFilters = normalizeContactsAdvancedFilters(filters);
  if (normalizedFilters.conditions.length) {
    snapshot[normalizedType] = normalizedFilters;
  } else {
    delete snapshot[normalizedType];
  }
  writeStorageSnapshot(storage, snapshot);
  return normalizedFilters;
};

export const clearContactsAdvancedFilterStorage = (storage = globalThis?.localStorage) => {
  if (!storage || typeof storage.removeItem !== "function") return;
  try {
    storage.removeItem(NCRM_CONTACTS_ADVANCED_FILTER_STORAGE_KEY);
  } catch (_) {
    /* ignore storage issues */
  }
};
